import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, Trash2 } from "lucide-react";
import { AdminAccounts } from "@/components/admin/AdminAccounts";
import { OnboardingVideoSettings } from "@/components/admin/OnboardingVideoSettings";
import { EmailHealth } from "@/components/admin/EmailHealth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEMS = [
  {
    key: "dashboard_metrics",
    label: "Dashboard metrics",
    note: "Earnings, sales stats and order/payment records used to compute them.",
  },
  {
    key: "test_students",
    label: "Test student accounts",
    note: "Only accounts whose email matches the pattern below. Real students are kept.",
  },
  {
    key: "test_enrollments",
    label: "Test enrollments",
    note: "Enrollments, progress and certificates of the matched test accounts.",
  },
  {
    key: "test_email_logs",
    label: "Test email logs",
    note: "Clears the Email Logs history. Nothing else is affected.",
  },
];

const PRESERVED = ["Courses", "Training", "Testimonials", "Leads / CRM data", "Pricing"];

export default function SettingsManager() {
  const [sel, setSel] = useState({
    dashboard_metrics: false,
    test_students: false,
    test_enrollments: false,
    test_email_logs: false,
  });
  const [pattern, setPattern] = useState("test");
  const [preview, setPreview] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings/reset-preview`, {
        params: { email_pattern: pattern },
        withCredentials: true,
      });
      setPreview(data);
    } catch {
      setPreview(null);
    }
  }, [pattern]);

  useEffect(() => {
    const t = setTimeout(loadPreview, 350);
    return () => clearTimeout(t);
  }, [loadPreview]);

  const anySelected = Object.values(sel).some(Boolean);

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/settings/reset-test-data`,
        { confirm: true, email_pattern: pattern, ...sel },
        { withCredentials: true },
      );
      setResult(data);
      setAck(false);
      setSel({ dashboard_metrics: false, test_students: false, test_enrollments: false, test_email_logs: false });
      loadPreview();
      toast.success(data.note);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const count = (k) => (preview ? preview[k] : null);

  return (
    <div data-testid="admin-settings-manager" className="space-y-5 max-w-2xl">
      <EmailHealth />

      <AdminAccounts />

      <OnboardingVideoSettings />

      <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5">
        <div className="flex items-center gap-2">
          <Trash2 className="h-4 w-4 text-rose-500" />
          <h3 className="font-display text-base font-semibold tracking-tight">Reset testing data</h3>
        </div>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          Tick only what you want to remove. Nothing else is touched.
        </p>

        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
            Test account email contains
          </label>
          <input
            data-testid="reset-email-pattern"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="test"
            className="mt-1.5 w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
          />
          <p className="mt-1.5 text-xs text-slate-500" data-testid="reset-match-count">
            {preview ? `${preview.test_students} student account(s) match` : "Checking matches…"}
          </p>
        </div>

        <div className="mt-4 space-y-2.5">
          {ITEMS.map((it) => (
            <label
              key={it.key}
              className="flex gap-3 rounded-xl border border-slate-200 dark:border-white/10 p-3 cursor-pointer hover:border-[#06B6D4]/50 transition-colors"
            >
              <input
                type="checkbox"
                data-testid={`reset-item-${it.key}`}
                checked={sel[it.key]}
                onChange={(e) => setSel({ ...sel, [it.key]: e.target.checked })}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                <span className="text-sm font-medium">
                  {it.label}
                  {count(it.key) !== null && (
                    <span className="ml-2 text-[11px] font-normal text-slate-500">
                      {count(it.key)} record(s)
                    </span>
                  )}
                </span>
                <span className="block text-xs text-slate-500 mt-0.5 leading-relaxed">{it.note}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="mt-4 rounded-xl bg-emerald-50 dark:bg-emerald-500/5 border border-emerald-200 dark:border-emerald-500/20 p-3">
          <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 text-xs font-semibold">
            <ShieldCheck className="h-3.5 w-3.5" /> Always preserved
          </div>
          <p className="mt-1 text-xs text-emerald-800/80 dark:text-emerald-300/70">
            {PRESERVED.join(" · ")}
          </p>
        </div>

        {anySelected && (
          <div
            data-testid="reset-confirm-box"
            className="mt-4 rounded-xl border border-rose-200 dark:border-rose-500/30 bg-rose-50/60 dark:bg-rose-500/5 p-4"
          >
            <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 text-sm font-semibold">
              <AlertTriangle className="h-4 w-4" /> Delete selected items only?
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="reset-ack"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="h-4 w-4"
              />
              I understand this will delete the selected data
            </label>
            <button
              type="button"
              onClick={reset}
              disabled={!ack || busy}
              data-testid="reset-test-data"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-rose-600 text-white px-5 py-2.5 text-xs font-semibold disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" /> {busy ? "Resetting…" : "Delete selected data"}
            </button>
          </div>
        )}
      </div>

      {result && (
        <div
          data-testid="reset-result"
          className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 text-xs"
        >
          <div className="font-semibold mb-2">Deleted</div>
          <ul className="space-y-1 text-slate-500">
            {Object.entries(result.deleted || {}).map(([k, v]) => (
              <li key={k}>
                {k}: {v}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-slate-500">{result.note}</p>
        </div>
      )}
    </div>
  );
}
